// Purging the deletions the undo history no longer holds (purgeDeletions), so soft-deleted rows go for good.

import { v1, toResult, sendOnLeave } from '@/infrastructure/api/v1/client';
import type { UndoStack } from './UndoStack';

/** The most ids one purgeDeletions call takes. */
const PURGE_BATCH = 500;

const PURGE_PATH = '/api/v1/deletions/purge' as const;

const batches = (ids: readonly string[]) => {
  const unique = [...new Set(ids)];
  const out: string[][] = [];
  for (let start = 0; start < unique.length; start += PURGE_BATCH) out.push(unique.slice(start, start + PURGE_BATCH));
  return out;
};

/** Purges these deletions; one that fails is left for the server, which purges a deletion a day after it was made. */
export function purgeDeletions(ids: readonly string[]): void {
  for (const batch of batches(ids)) {
    void toResult(v1.POST(PURGE_PATH, { body: { uniqueIds: batch } })).then(({ error }) => {
      if (error) console.warn('[undo] could not purge deletions', error);
    });
  }
}

/** Purges these deletions as the page goes away: keepalive, so the request outlives the page. */
export function purgeDeletionsOnLeave(ids: readonly string[]): void {
  // One that fails is purged by the server a day later
  for (const batch of batches(ids)) sendOnLeave('POST', PURGE_PATH, { uniqueIds: batch });
}

const watched = new WeakSet<object>();

/** Purges what `undo` no longer holds as it lets go, and what it still holds as the page goes away. Once per stack. */
export function watchDeletions(undo: Pick<UndoStack, 'purges$' | 'deletionIds'>): void {
  if (watched.has(undo)) return;
  watched.add(undo);
  undo.purges$.subscribe(ids => purgeDeletions(ids));
  window.addEventListener('pagehide', () => purgeDeletionsOnLeave(undo.deletionIds()));
}
